import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Briefcase,
  Hammer,
  FileText,
  Receipt,
  Edit2,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

// Web & Marketing Project Types
const webProjectTypes = [
  { name: 'Website Design', count: 8 },
  { name: 'SEO Campaign', count: 5 },
  { name: 'E-commerce Store', count: 6 },
  { name: 'Landing Page', count: 4 },
  { name: 'Social Media Management', count: 3 },
  { name: 'Branding & Identity', count: 4 }
];

const webQuoteTemplates = [
  'Standard Web Quote',
  'SEO Service Quote',
  'Monthly Retainer Quote'
];

// Construction Project Types
const constructionProjectTypes = [
  { name: 'Home Renovation', count: 12 },
  { name: 'New Construction', count: 15 },
  { name: 'Kitchen Remodel', count: 10 },
  { name: 'Bathroom Remodel', count: 8 },
  { name: 'Roofing', count: 6 },
  { name: 'Deck & Patio', count: 7 }
];

const constructionQuoteTemplates = [
  'Construction Estimate',
  'Material & Labor Quote',
  'Change Order Form'
];

const FormTemplatesIndex = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-5xl mx-auto" data-testid="form-templates-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Form Templates</h1>
        <p className="text-slate-500 mt-1">
          Manage project types and quote templates by business type
        </p>
      </div>

      {/* Business Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Web & Marketing */}
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <Briefcase size={28} className="text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-slate-900">Web & Marketing</CardTitle>
                <CardDescription className="text-slate-600">
                  Templates for digital agencies
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Project Types</h4>
              <ul className="space-y-2">
                {webProjectTypes.map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 flex items-center gap-2">
                      <FileText size={14} className="text-blue-500" />
                      {item.name}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {item.count} fields
                    </span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-slate-100 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Quote Templates</h4>
                <ul className="space-y-2">
                  {webQuoteTemplates.map((item, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                      <Receipt size={14} className="text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Button 
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700" 
              data-testid="manage-web-templates-btn"
              onClick={() => navigate('/settings/templates/web')}
            >
              <Edit2 size={16} className="mr-2" />
              Manage Templates
              <ArrowRight size={16} className="ml-auto" />
            </Button>
          </CardContent>
        </Card>

        {/* Construction Contractor */}
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-600 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <Hammer size={28} className="text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-slate-900">Construction Contractor</CardTitle>
                <CardDescription className="text-slate-600">
                  Templates for construction businesses
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Project Types</h4>
              <ul className="space-y-2">
                {constructionProjectTypes.map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 flex items-center gap-2">
                      <FileText size={14} className="text-amber-500" />
                      {item.name}
                    </span>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      {item.count} fields
                    </span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-slate-100 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Quote Templates</h4>
                <ul className="space-y-2">
                  {constructionQuoteTemplates.map((item, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                      <Receipt size={14} className="text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Button 
              className="w-full mt-6 bg-amber-600 hover:bg-amber-700" 
              data-testid="manage-construction-templates-btn"
              onClick={() => navigate('/settings/templates/construction')}
            >
              <Edit2 size={16} className="mr-2" />
              Manage Templates
              <ArrowRight size={16} className="ml-auto" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-slate-200 shadow-sm bg-slate-50 mt-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-blue-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-slate-900">About Form Templates</h4>
              <p className="text-sm text-slate-600 mt-1">
                Form templates define the fields and structure for projects and quotes based on business type. 
                When a tenant is created with a specific business type, they will have access to the corresponding 
                project types and quote templates. Changes here affect all new projects created by tenants.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormTemplatesIndex;
